import { printableAdmins } from "../dailyPrintout/render/adminsBlock";
import { timeOffReasonOf } from "../dailyPrintout/util/timeOffReason";
import type { BuildContext } from "./aiPrintoutBuildContext";
import { buildCensus, censusTotalOf } from "./aiPrintoutCensus";
import { mapUnmatchedShift } from "./aiPrintoutCounterparts";
import { buildSideMetrics, createShiftHppdFields } from "./aiPrintoutMetrics";
import { mapGap, mapRequirement } from "./aiPrintoutRequirements";
import { mapCallOut, mapPtoShift, mapStaffRow } from "./aiPrintoutRowMappers";
import { createShiftFacts } from "./aiPrintoutShiftFacts";
import type { AiPrintoutData } from "./contract";

/** The rows the Detailed printout itself lists: people, unfilled positions, time off and call outs. */
export const buildRows = (build: BuildContext): Pick<AiPrintoutData, "shifts" | "gaps" | "pto" | "callOuts"> => {
	const { input, context, source, orders, keysOf } = build;
	const { department, hospitalData, jobManagementEnabled, labels } = input;
	// `assembleSourceRows` emits one gap row per missing slot, in the same order, ahead of the staff rows.
	const gapRows = source.rows.filter((row) => !row.nurse);
	const shiftFacts = createShiftFacts({
		isActual: input.isActual,
		counterparts: build.counterparts,
		timeGroupKeysOf: keysOf,
		hppdFieldsOf: createShiftHppdFields(build.statsInputs, build.unionByNurseId),
		nurses: hospitalData.nurses,
		department,
		jobManagementEnabled,
		context,
	});
	return {
		shifts: source.rows.flatMap((row) =>
			row.nurse
				? [
						{
							...mapStaffRow({ row, department, jobManagementEnabled, context, orders, findDepartmentLocation: input.findDepartmentLocation }),
							...shiftFacts(row.nurse),
						},
					]
				: [],
		),
		gaps: source.missingLocations.map((slot, index) => mapGap({ slot, row: gapRows[index], department, labels, context, orders, timeGroupKeysOf: keysOf })),
		pto: input.ptoShifts.map((shift) =>
			mapPtoShift({
				shift,
				// As the Detailed printout: no reason unless the viewer prints them, since one can be medical.
				reason: input.includeTimeOffReason ? timeOffReasonOf(shift, hospitalData.nurses, department.dayStart) : null,
				timeGroupKeys: keysOf(shift.startTime, shift.endTime),
				context,
			}),
		),
		callOuts: input.callOuts.map((callOut) =>
			mapCallOut({
				callOut,
				hospitalData,
				lastNameFirst: input.lastNameFirst,
				callOffSourceLabel: input.callOffSourceLabel,
				timeGroupKeys: keysOf(callOut.startTime, callOut.endTime),
				context,
			}),
		),
	};
};

/** Census and both sides' metrics, from the Day view's stats and the Detailed source rows' location census. */
export const buildDayMetrics = (build: BuildContext): Pick<AiPrintoutData, "census" | "metrics"> => {
	const { input, sides, orders } = build;
	const census = censusTotalOf(input.dayStats.census);
	const side = (isActual: boolean) =>
		buildSideMetrics({
			inputs: build.statsInputs,
			isActual,
			shifts: isActual ? sides.punches : sides.scheduled,
			dayStats: isActual ? input.dayStatsActual : input.dayStats,
			census,
			wagesVisible: isActual ? input.wagesVisible.actual : input.wagesVisible.scheduled,
			weekday: build.scheduleDay.weekday,
			orders,
			unionByNurseId: build.unionByNurseId,
		});
	return {
		census: buildCensus({
			dayCensus: input.dayStats.census,
			censusByLocationName: build.source.censusByLocationName,
			readings: input.censusReadings,
			orders,
		}),
		metrics: { scheduled: side(false), actual: side(true) },
	};
};

/** The other side's leftovers, the positions the day needs, the time groups and the viewer's header admins. */
export const buildDayExtras = (build: BuildContext): Pick<AiPrintoutData, "unmatched" | "requirements" | "timeGroups" | "admins"> => {
	const { input, context, orders, keysOf, counterparts } = build;
	const { department, jobManagementEnabled, isActual } = input;
	return {
		unmatched: counterparts.unmatched.map((shift) =>
			mapUnmatchedShift({ shift, isActual: !isActual, isStandIn: counterparts.isStandIn(shift), department, jobManagementEnabled, context }),
		),
		requirements: build.requiredSlots.map((required) =>
			mapRequirement({ required, department, labels: input.labels, context, orders, timeGroupKeysOf: keysOf }),
		),
		timeGroups: build.timeGroups === null ? null : build.timeGroups.groups,
		admins: input.showAdmins.enabled ? printableAdmins(input.showAdmins).map(({ role, name }) => ({ role, name })) : null,
	};
};
